import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { StyledSnackbar } from "components/StyledComponents/styledComponents";
import { StyledAlert } from "components/StyledComponents/styledComponents";
import {
  TableContainer,
  Paper,
  Grid,
  TextField,
  Box,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import Autocomplete from "@mui/material/Autocomplete";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { CustomPaper } from "components/Styles/globalStyles";
import Cookie from "js-cookie";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  inputStyle,
  StyledButton,
  StyledTypography,
  StyledAgGridContainer,
  ViewButton,
  DeleteButton,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";

export default function ApproveSalaryAdvance() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [employeeData, setEmployeeData] = useState([]);
  const [openApprove, setOpenApprove] = useState(false);
  const [approveMessage, setApproveMessage] = useState("");
  const [approveStatus, setApproveStatus] = useState("success");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const hrms_token = Cookie.get("hrms_token");
  const navigate = useNavigate();
  const MInput = inputStyle();
  const ConfirmationDialog = ({ open, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <DialogContentText sx={dialogContentTextStyle}>
          Are you sure you want to reject this previously approved advance?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <StyledButton onClick={onClose} color="primary">
          Cancel
        </StyledButton>
        <StyledButton onClick={onConfirm} color="error" autoFocus>
          Confirm
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
  ConfirmationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
  };

  useEffect(() => {
    fetchEmployeeData(0);
  }, []);

  const handleCloseApproveSnackbar = () => {
    setOpenApprove(false);
  };

  const showNotification = (message, type = "success") => {
    setApproveMessage(message);
    setApproveStatus(type);
    setOpenApprove(true);
  };

  const fetchEmployeeData = async (companyId) => {
    setLoading(true);
    try {
      const response = await fetch(URLs.hrmsAdvanceDetailsUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || `HTTP error! Status: ${response.status}`;
        } catch {
          errorMessage = errorText || `HTTP error! Status: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Invalid data format received from server");
      }

      const formattedData = data.map((item) => ({
        employeeId: item.employeeRecordId || item.employeeId,
        advanceId: item.advanceId,
        name: item.employeeName || "-",
        newAdvance: parseFloat(item.advanceAmount || 0),
        deductionPerMonth: parseFloat(item.deductionAmount || 0),
        approvedStatus: item.advanceApprovedStatus || "P",
        remarks: item.remarks || "No remarks available",
        date: item.date
          ? new Date(item.date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-",
      }));

      setEmployeeData(formattedData);
    } catch (error) {
      console.error("Error fetching employee data:", error);
      showNotification(`Failed to fetch employee data: ${error.message}`, "error");
      setEmployeeData([]); // Clear data on error
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalStatus = async (employeeId, status) => {
    if (!employeeId) {
      showNotification("Employee ID is required", "error");
      return;
    }

    const apiStatus = status === "Approved" ? "A" : status === "Rejected" ? "R" : "P";
    const displayStatus = status; // Store the display status

    const currentStatus = employeeData.find(
      (item) => item.employeeId === employeeId
    )?.approvedStatus;

    // Convert current status for comparison
    const currentDisplayStatus =
      currentStatus === "A" ? "Approved" : currentStatus === "R" ? "Rejected" : "Pending";

    if (status === "Rejected" && currentDisplayStatus === "Approved") {
      setPendingAction({ employeeId, status: displayStatus, apiStatus });
      setDialogOpen(true);
      return;
    }

    await processApproval(employeeId, displayStatus, apiStatus);
  };

  const processApproval = async (employeeId, displayStatus, apiStatus) => {
    try {
      showNotification(`Processing ${displayStatus.toLowerCase()} request...`, "info");
      setLoading(true);

      // Optimistically update UI with display status
      setEmployeeData((prevData) =>
        prevData.map((item) =>
          item.employeeId === employeeId ? { ...item, approvedStatus: apiStatus } : item
        )
      );

      // Find the selected employee's data to get the advanceId
      const selectedEmployee = employeeData.find((item) => item.employeeId === employeeId);
      if (!selectedEmployee) {
        throw new Error("Employee data not found");
      }

      const requestBody = {
        employeeRecordId: employeeId,
        advanceId: selectedEmployee.advanceId,
        advanceApprovedStatus: apiStatus,
      };

      const response = await fetch(URLs.hrmsApproveOrRejectdvance, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || "Server error occurred";
        } catch {
          errorMessage = responseText || `Failed to ${displayStatus.toLowerCase()} advance`;
        }
        throw new Error(errorMessage);
      }

      showNotification(`Successfully ${displayStatus.toLowerCase()}d the advance`, "success");

      // Refresh the data
      await fetchEmployeeData();
    } catch (error) {
      console.error("Error updating approval status:", error);
      showNotification(
        `Failed to ${displayStatus.toLowerCase()} the advance: ${error.message}`,
        "error"
      );
      await fetchEmployeeData();
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  const handleDialogConfirm = () => {
    if (pendingAction) {
      const { employeeId, status, apiStatus } = pendingAction;
      processApproval(employeeId, status, apiStatus);
      setPendingAction(null);
    }
  };

  const ApprovalStatusCellRenderer = (props) => {
    const status = props.data.approvedStatus;
    const displayStatus = status === "A" ? "APPROVED" : status === "R" ? "REJECTED" : "PENDING";
    const statusColor = status === "A" ? "green" : status === "R" ? "red" : "orange";

    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        {status === "A" && <CheckCircleIcon style={{ color: statusColor }} />}
        {status === "R" && <CancelIcon style={{ color: statusColor }} />}
        {status === "P" && <span style={{ color: statusColor }}>●</span>}
        <span style={{ marginLeft: "8px", color: statusColor }}>{displayStatus}</span>
      </div>
    );
  };

  const ActionCellRenderer = (props) => {
    const status = props.data.approvedStatus;

    return (
      <div style={{ display: "flex", justifyContent: "center", gap: "8px", alignItems: "center" }}>
        {status === "P" && (
          <>
            <Tooltip title="Approve">
              <IconButton
                onClick={() => handleApprovalStatus(props.data.employeeId, "Approved")}
                color="primary"
                size="small"
              >
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject">
              <IconButton
                onClick={() => handleApprovalStatus(props.data.employeeId, "Rejected")}
                color="error"
                size="small"
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  const ViewRemarksRenderer = (props) => (
    <div style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}>
      <Tooltip title="View Remarks">
        <IconButton
          onClick={() => {
            setSelectedRemarks(props.value);
            setRemarksDialogOpen(true);
          }}
          size="small"
        >
          <VisibilityIcon style={{ color: "#1a73e8" }} />
        </IconButton>
      </Tooltip>
    </div>
  );
  ViewRemarksRenderer.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const columnDefs = [
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      filter: true,
      flex: 1,
      sort: "desc",
    },
    {
      headerName: "Employee ID",
      field: "employeeId",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Name",
      field: "name",
      sortable: true,
      filter: true,
      flex: 1.5,
    },

    {
      headerName: "New Advance",
      field: "newAdvance",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => {
        const value = parseFloat(params.value || 0);
        return `₹ ${value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Deduction per Month",
      field: "deductionPerMonth",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => {
        const value = parseFloat(params.value || 0);
        return `₹ ${value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Status",
      field: "approvedStatus",
      cellRenderer: ApprovalStatusCellRenderer,
      sortable: true,
      filter: true,
      flex: 1,
      filterParams: {
        valueGetter: (params) => {
          const status = params.data?.approvedStatus;
          return status === "A"
            ? "Approved"
            : status === "R"
            ? "Rejected"
            : status === "P"
            ? "Pending"
            : "";
        },
      },
    },
    {
      headerName: "Remarks",
      field: "remarks",
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: ViewRemarksRenderer,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      sortable: false,
      filter: false,
      width: 150,
      cellStyle: { display: "flex", justifyContent: "center", padding: "5px" },
    },
  ];

  return (
    <DashboardLayout>
      <StyledTableContainer>
        <Dialog
          open={loading}
          onClose={() => setLoading(false)}
          aria-labelledby="loading-dialog-title"
        >
          <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <CircularProgress color="primary" />
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Loading, please wait...
            </Typography>
          </DialogContent>
        </Dialog>
        <StyledAgGridContainer sx={{ flexGrow: 1 }} component={Paper} className={`ag-theme-alpine`}>
          <AgGridReact
            rowData={employeeData}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={10}
            suppressCellFocus={true}
            defaultColDef={{
              resizable: true,
              filter: true,
            }}
            onGridReady={(params) => {
              params.api.sizeColumnsToFit();
            }}
            onFirstDataRendered={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </StyledAgGridContainer>

        <StyledSnackbar
          open={openApprove}
          autoHideDuration={5000}
          onClose={handleCloseApproveSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <StyledAlert
            onClose={handleCloseApproveSnackbar}
            severity={approveStatus}
            sx={{ width: "100%" }}
          >
            {approveMessage}
          </StyledAlert>
        </StyledSnackbar>
        <ConfirmationDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleDialogConfirm}
        />
        <Dialog open={remarksDialogOpen} onClose={() => setRemarksDialogOpen(false)}>
          <DialogTitle>Remarks</DialogTitle>
          <DialogContent>
            <DialogContentText>{selectedRemarks}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <StyledButton onClick={() => setRemarksDialogOpen(false)}>Close</StyledButton>
          </DialogActions>
        </Dialog>
      </StyledTableContainer>
    </DashboardLayout>
  );
}

ApproveSalaryAdvance.propTypes = {
  data: PropTypes.shape({
    approvedStatus: PropTypes.any, // Change to the appropriate type if known (e.g., PropTypes.string)
    employeeId: PropTypes.any, // Change to the appropriate type if known (e.g., PropTypes.number)
  }).isRequired,
};
